/**
 * Where sign-in sends you afterwards, and why it is an allowlist.
 *
 * `RequireSession` (`session.tsx`) redirects a signed-out visitor to `/auth/login` carrying
 * `state.from` — the address they were actually trying to open. This decides whether to honour it.
 *
 * ## It is an allowlist because the alternative is an open redirect
 *
 * `from` arrives in router state rather than the query string, so it is not quite as exposed as the
 * classic `?next=` hole — but it is still a destination this app navigates to on somebody's behalf,
 * and the rule that keeps it safe is that only paths inside this application are honoured. Anything
 * else falls back to the console's home. In particular `//evil.example` — a protocol-relative URL
 * that a browser resolves to ANOTHER ORIGIN — must never survive, and the prefix test is what stops
 * it: `//evil.example` does not begin with `/app` or `/adm`.
 *
 * ## `/adm` is here because leaving it out was a bug
 *
 * The check was `from.startsWith('/app')` alone, written before `/adm` existed (ADR 0177). Both are
 * guarded by `RequireSession`, so both can redirect somebody to sign in — but only one of them was
 * honoured on the way back. A staff member opening `/adm` while signed out was sent to the login
 * screen, signed in, and landed on the tenant dashboard, with nothing on the screen explaining why
 * and no trace of where they had been going. They had to know to type the address a second time.
 *
 * Adding a route that `RequireSession` guards therefore means adding it here too, which is the
 * reason this is one exported constant and one exported function rather than an expression inlined
 * at the single call site: the next person gets something to find.
 */

/** Where a signed-in person goes when there is nowhere better — the tenant console's home. */
export const SIGNED_IN_HOME = '/app'

/**
 * The areas `RequireSession` guards, and so the only areas sign-in will return to.
 *
 * Prefixes rather than exact paths, because the value carries a full path and query — `/app`,
 * `/app/settings/security`, `/adm/tenants?status=suspended` are all legitimate.
 */
const RETURNABLE = ['/app', '/adm'] as const

/**
 * The path to send somebody to after they sign in.
 *
 * Takes the raw `location.state.from`, which is `unknown` because router state is whatever the
 * previous screen put there — including nothing at all on a direct visit to `/auth/login`.
 */
export function returnTo(from: unknown): string {
  if (typeof from !== 'string') return SIGNED_IN_HOME
  // Belt and braces. The prefixes below already exclude `//evil.example`, but this states the
  // property being defended so that widening `RETURNABLE` later cannot quietly reopen it.
  if (from.startsWith('//')) return SIGNED_IN_HOME
  return RETURNABLE.some((prefix) => from.startsWith(prefix)) ? from : SIGNED_IN_HOME
}
